import React, { useState, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  Box,
  CardMedia,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import MUITypography from "../Components/MUITypography";
import {
  APPOINTMENTS,
  FOLLOWUPS,
  NO_LOGIN_ROUTES,
  ROUTE_APPOINTMENTSFollwUps,
  ROUTE_EVENTS,
  ROUTE_LOGIN,
  ROUTE_PROFILE,
  ROUTE_SUBSCRIPTION,
  ROUTE_UPDATE_CHECK_LIST,
  ROUTE_UPDATE_TASK,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  SESSION_USER_TYPE_KEY,
} from "../Util/VMConstants";
import { checkIsPermissionExist, isAdminLoggedIn, isEmpty } from "../Util/UtilHelper";
import StorageFactory from "../Util/StorageFactory";
import { clearAuthToken } from "../Util/jwtAuth";
import { align_right, VM_Colors } from "../Styles/VMStyles";
import {
  formatExpiresInDaysLabel,
  getSubscriptionStatusExpiryDate,
} from "../Util/subscriptionTierHelper";

function NavbarToolbarSkeleton({ isMobile }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Skeleton variant="rounded" width={70} height={28} />

      {!isMobile && (
        <>
          <Skeleton
            variant="rounded"
            width={100}
            height={32}
            sx={{ borderRadius: "18px" }}
          />
          <Skeleton
            variant="rounded"
            width={90}
            height={32}
            sx={{ borderRadius: "18px" }}
          />
          <Skeleton
            variant="rounded"
            width={70}
            height={32}
            sx={{ borderRadius: "18px" }}
          />
        </>
      )}

      <Stack spacing={0.5} sx={{ minWidth: 72 }}>
        <Skeleton variant="text" width={80} sx={{ fontSize: "1rem" }} />
        <Skeleton variant="text" width={50} sx={{ fontSize: "0.875rem" }} />
      </Stack>

      <Skeleton variant="circular" width={40} height={40} />
    </Stack>
  );
}

export default function Navbar({ toggleDrawer = () => {}, registrationUser = null }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const { permissions, user: reduxUser } = useSelector(
    (state) => state.subscription
  );
  const user = registrationUser
    ? { ...reduxUser, ...registrationUser }
    : reduxUser;
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isRoomsRegistration = location?.pathname
    ?.toLowerCase()
    .includes("/roomsregistration");

  /** ✅ MEMOIZED STORAGE */
  const sessionFactory = useMemo(
    () => new StorageFactory("session"),
    []
  );

  const sessionUserId = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const isToolbarLoading =
    !isEmpty(sessionUserId) && isEmpty(user.id) && isEmpty(registrationUser);
  const isTaskChecklistPermission = checkIsPermissionExist(
    VM_PERMISSIONS,
    "VM047"
  );
  const isAllEnquiriesPermission = checkIsPermissionExist(
    VM_PERMISSIONS,
    "VM041"
  );

  const subscriptionStatus = useSelector(
    (state) => state.subscription.status
  );
  const displaySubscriptionStatus =
    registrationUser && reduxUser.id !== registrationUser.id
      ? null
      : subscriptionStatus;

  const expiryInDaysLabel = useMemo(() => {
    const expiryDate = getSubscriptionStatusExpiryDate(displaySubscriptionStatus);
    return formatExpiresInDaysLabel(expiryDate);
  }, [displaySubscriptionStatus]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    clearAuthToken();
    sessionFactory.clear();
    handleMenuClose();
    navigate("/" + ROUTE_LOGIN, { replace: true });
  };

  const hideLoginButton = NO_LOGIN_ROUTES.some((route) =>
    location.pathname.toLowerCase().startsWith(route)
  );

  if (isRoomsRegistration) {
    return (
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: VM_Colors.white,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          ...align_right
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => navigate("/" + ROUTE_LOGIN, { replace: true })}
            sx={{
              textTransform: "none",
              bgcolor: "#e6f0ff",
              color: "#000",
              fontSize: "16px",
              pl: "30px",
              pr: "30px",
              fontWeight: 600,
              "&:hover": { bgcolor: "#dbe8fa" },
            }}
          >
            Click Here to Login
          </Button>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        backgroundColor: VM_Colors.white,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          overflowX: "auto",
        }}
      >
        {/* Menu Icon + Logo */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ width: 25, ml: 2, mr: 1 }}
            >
              <MenuIcon sx={{ color: VM_Colors.primary }} />
            </IconButton>
          )}

          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/landing", { replace: true })}
          >
            <Avatar
              src={"/static/logo.png"}
              alt="Logo"
              sx={{ width: 50, height: 40, mr: 1 }}
            />
            <CardMedia
              component="img"
              image={"/static/img_logo_text_1_1.png"}
              alt="Text Logo"
              sx={{ height: 30, objectFit: "contain" }}
            />
          </Box>

          {isToolbarLoading && (
            <Skeleton
              variant="rounded"
              width={70}
              height={24}
              sx={{ ml: 2 }}
            />
          )}

          {!isToolbarLoading &&
            !isEmpty(user.id) &&
            (displaySubscriptionStatus?.plan === "free" ||
              displaySubscriptionStatus?.plan === "trial") && (
              <Box sx={{ ml: 2, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <CardMedia
                  component="img"
                  image="/static/free_trial.svg"
                  sx={{ width: 70 }}
                />
                {expiryInDaysLabel ? (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.25,
                      color: VM_Colors.steel_gray || "#6B7280",
                      fontWeight: 600,
                      fontSize: 11,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {expiryInDaysLabel}
                  </Typography>
                ) : null}
              </Box>
            )}
        </Box>

        {/* RIGHT */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isToolbarLoading ? (
            <NavbarToolbarSkeleton isMobile={isMobile} />
          ) : !isEmpty(user.id) ? (
            <>
              { (displaySubscriptionStatus?.plan === "free" || displaySubscriptionStatus?.plan === "trial") ? (
                isAdminLoggedIn(userType) ? (
                <CardMedia
                  component="img"
                  image="/static/subscribe_now.svg"
                  sx={{ width: 70, mr: 2, cursor: "pointer" }}
                  onClick={() => navigate("/" + ROUTE_SUBSCRIPTION)}
                />
                ) : null
              ) : displaySubscriptionStatus?.plan === "premium" ? (
                <CardMedia
                  component="img"
                  image="/static/premium.svg"
                  sx={{ width: 100, mr: 2, cursor: "pointer" }}
                  onClick={() => navigate("/" + ROUTE_SUBSCRIPTION)}
                />
              ) : null}

              {!isMobile && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: 2 }}>
                  {checkIsPermissionExist(permissions, "VM010") && (
                    <Button
                      onClick={() =>
                        navigate({
                          pathname: "/" + ROUTE_APPOINTMENTSFollwUps,
                          search: `?type=${APPOINTMENTS}`,
                        })
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: 13,
                        fontWeight: 600,
                        px: 2,
                        py: 0.5,
                        borderRadius: "18px",
                        backgroundColor: "#e9f1ff",
                        color: "#0a4ea3",
                        border: "1px solid #d1e2ff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        "&:hover": {
                          backgroundColor: "#dce8ff",
                        },
                      }}
                    >
                      Appointments
                    </Button>
                  )}

                  {checkIsPermissionExist(permissions, "VM010") && (
                    <Button
                      onClick={() =>
                        navigate({
                          pathname: "/" + ROUTE_APPOINTMENTSFollwUps,
                          search: `?type=${FOLLOWUPS}`,
                        })
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: 13,
                        fontWeight: 600,
                        px: 2,
                        py: 0.5,
                        borderRadius: "18px",
                        backgroundColor: "#e9f1ff",
                        color: "#0a4ea3",
                        border: "1px solid #d1e2ff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        "&:hover": {
                          backgroundColor: "#dce8ff",
                        },
                      }}
                    >
                      Follow-ups
                    </Button>
                  )}

                  {checkIsPermissionExist(permissions, "VM010") && (
                    <Button
                      onClick={() =>
                        navigate({
                          pathname: "/" + ROUTE_EVENTS,
                          search: "?type=events",
                        })
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: 13,
                        fontWeight: 600,
                        px: 2,
                        py: 0.5,
                        borderRadius: "18px",
                        backgroundColor: "#e9f1ff",
                        color: "#0a4ea3",
                        border: "1px solid #d1e2ff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        "&:hover": {
                          backgroundColor: "#dce8ff",
                        },
                      }}
                    >
                      Events
                    </Button>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <MUITypography
                  label={user.name || ""}
                  fontWeight={800}
                  sx={{ color: VM_Colors.black, paddingBottom: "2px" }}
                />
                <MUITypography label={user.userType || ""} color="gray" />
              </Box>

              <Box>
                <IconButton onClick={handleMenuOpen}>
                  {isEmpty(user.profilePic) ? (
                    <AccountCircleIcon
                      sx={{
                        color: VM_Colors.primary,
                        border: 2,
                        borderRadius: "50%",
                        borderColor: VM_Colors.primary,
                        fontSize: 30,
                        width: "40px",
                        height: "40px",
                      }}
                    />
                  ) : (
                    <Avatar
                      src={user.profilePic}
                      alt="Profile"
                      sx={{
                        width: 50,
                        height: 50,
                        fontSize: 36,
                        border: "0.1px dotted gray",
                      }}
                    />
                  )}

                  <ExpandMoreIcon sx={{ color: VM_Colors.primary }} />
                </IconButton>
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => navigate("/" + ROUTE_PROFILE)}>
                  My Profile
                </MenuItem>
                {
                  isAdminLoggedIn(userType)  &&
                  <MenuItem onClick={() => navigate("/" + ROUTE_SUBSCRIPTION)}>
                    My Subscription
                  </MenuItem>
                }

                {/* {isTaskChecklistPermission && ( */}
                  <>
                    <MenuItem onClick={() => navigate("/" + ROUTE_UPDATE_TASK)}>
                      My Tasks
                    </MenuItem>
                    <MenuItem
                      onClick={() => navigate("/" + ROUTE_UPDATE_CHECK_LIST)}
                    >
                      My Checklists
                    </MenuItem>
                  </>
                {/* )} */}

                {isMobile && isAllEnquiriesPermission && (
                  <>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        navigate({
                          pathname: "/" + ROUTE_APPOINTMENTSFollwUps,
                          search: `?type=${APPOINTMENTS}`,
                        });
                      }}
                    >
                      Appointments
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        navigate({
                          pathname: "/" + ROUTE_APPOINTMENTSFollwUps,
                          search: `?type=${FOLLOWUPS}`,
                        });
                      }}
                    >
                      Follow-ups
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        navigate({
                          pathname: "/" + ROUTE_EVENTS,
                          search: `?type=events`,
                        });
                      }}
                    >
                      Events
                    </MenuItem>
                  </>
                )}

                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          ) : !hideLoginButton && isEmpty(userType)? (
            <Button
              variant="contained"
              onClick={() => {
                if (isMobile) toggleDrawer();
                navigate("/" + ROUTE_LOGIN, { replace: true });
              }}
              sx={{
                textTransform: "none",
                bgcolor: "#e6f0ff",
                color: "#000",
                fontSize: "18px",
                pl: "30px",
                pr: "30px",
                fontWeight: 600,
                "&:hover": { bgcolor: "#dbe8fa" },
              }}
            >
              {/* {showSkip ? "Skip" : "Login"} */}
              {"Click here to login"}
            </Button>
          ) : null}
        </Box>
      </Toolbar>
    </AppBar>
  );
}